import React, { Component } from 'react'
import PropTypes from 'prop-types'
import mime from 'mime-types'
import { cx } from '@emotion/css'
import ClipIcon from '@offerzen/zenicon/ClipIcon'
import Attachment from '../Attachment'
import Badge from '../Badge'
import FormErrorMessage from '../FormErrorMessage'
import FormHelpText from '../FormHelpText'

import styles, { labelColor } from './styles'

class SingleFileUpload extends Component {
  // eslint-disable-next-line react/state-in-constructor
  state = {
    error: null,
  }

  handleUpload = ev => {
    const file = ev.target.files[0]

    // Make sure there is a file
    if (!file) {
      this.setState({ error: 'No file attached. Try again.' })
      return
    }

    // Only allow files smaller than 10MB
    if (file.size >= 10485760) {
      this.setState({ error: 'The file exceeds the 10MB limit. Try a different file.' })
      return
    }

    const reader = new FileReader()

    reader.onloadend = async output => {
      const params = {
        name: file.name,
        url: output.target.result,
      }

      // If there's no file type in the data URL, look it up and force it
      if (params.url.indexOf('data:;') !== -1) {
        const mimeType = mime.lookup(file.name)
        if (!mimeType) {
          // If the mime type cannot be recognized, error
          this.setState({ error: 'The file type is not recognised. Try a different file.' })
          return
        }
        params.url = params.url.replace('data:;', `data:${mimeType};`)
      }

      // If file is valid, clear error
      this.setState({ error: null })

      const { input, ...props } = this.props
      const handler = input.onChange || props.onChange
      if (handler) handler(params)
    }

    reader.readAsDataURL(file)
  }

  clearUpload = () => {
    // Clear errors, just in case
    this.setState({ error: null })

    const { input, ...props } = this.props
    const handler = input.onChange || props.onChange
    if (handler) handler(null)
  }

  render() {
    const { theme, label, meta, input, helpText, ...props } = this.props
    const value = input.value || props.value
    const errored = (meta.touched && meta.error) || this.state.error
    const error = meta.touched ? meta.error : this.state.error

    return (
      <div className={cx(styles.wrapper, props.className)}>
        {value ? (
          <Attachment name={value.name} url={value.url} theme={theme} size="small" onClear={this.clearUpload} />
        ) : (
          <label htmlFor={props.id} className={cx(styles.label, errored ? styles.labelError : labelColor(theme))}>
            <Badge icon={ClipIcon} size="default" theme={errored ? 'error' : theme} />
            <span className={styles.labelText}>
              {label}
              {Boolean(props.optional) && <> (optional)</>}
            </span>

            <input
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...input}
              id={props.id}
              type="file"
              value={undefined}
              onChange={this.handleUpload}
              className={styles.input}
            />
          </label>
        )}
        {Boolean(helpText && !errored) && <FormHelpText text={helpText} />}
        {Boolean(errored) && <FormErrorMessage text={error} />}
      </div>
    )
  }
}

SingleFileUpload.propTypes = {
  id: PropTypes.string.isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
  value: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string,
  }),
  input: PropTypes.shape({
    onChange: PropTypes.func,
    value: PropTypes.oneOfType([
      PropTypes.shape({
        value: PropTypes.string,
        label: PropTypes.string,
      }),
      PropTypes.string,
    ]),
  }),
  onChange: PropTypes.func,
  label: PropTypes.string,
  optional: PropTypes.bool,
  theme: PropTypes.oneOf(['', 'primary', 'secondary', 'success', 'warning', 'error', 'legacy', 'light', 'dark', 'tertiary', 'info']),
  helpText: PropTypes.string,
  className: PropTypes.string,
}

SingleFileUpload.defaultProps = {
  meta: {},
  input: {},
  label: 'Attach a file',
  theme: 'primary',
}

export default SingleFileUpload
